import {
  Button,
  Alert,
  Table,
  TableHead,
  TableRow,
  TableBody,
  Typography,
} from '@mui/material';
import React, {PropsWithChildren, useEffect, useState} from 'react';
import TableCell from '@mui/material/TableCell';
import {camelCase} from 'lodash';
import clsx from 'clsx';
import theme, {themeColors} from '../../theme/Theme';
import useModelPortfolios from './useModelPortfolios';
import {
  GetModelPortfolios,
  GetModelPortfolios_modelPortfolios,
  GetModelPortfolios_modelPortfolios_providerContact,
} from '../../schema-types/GetModelPortfolios';
import Loading from '../../components/Loading/Loading';
import {UPDATES} from '../../shared/UPDATES';
import {recommendFieldsType} from '../../features/recommendations/CreateRecommend/recommendFields';
import type {modelPortfolioField} from '../../features/recommendations/RecommendModify/modifyFieldTypes';
import {PotDetails} from '../../schema-types/PotDetails';
import {useAutoForm} from '../../components/AutoForm/useAutoForm';
import {SaltusAny} from '../../utils/SaltusAny';
import {useReviewContext} from '../../contexts/ReviewContext';
import {useRTQ} from '../../hooks/useRTQ';
import {useAccountContext} from '../../contexts/AccountContext';

const classes = {
  table: {
    backgroundColor: 'white',
    border: 0,
    '& thead td': {
      flex: 0,
      fontWeight: 'bold',
    },
    '& td': {
      border: 0,
      textAlign: 'center',
      padding: 0,
      width: 80,
    },
    '& td:first-child': {
      width: 300,
      padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
      textAlign: 'left',
      fontWeight: 'bold',
    },
    '& td.selected, & td.selected.shareAtr': {
      backgroundColor: theme.palette.primary.main,
      '& button': {
        color: 'white',
        fontWeight: 'bold',
        transform: `scale(1.3)`,
      },
    },
    '& td.shareHover': {
      backgroundColor: theme.palette.grey[200],
    },
    '& td.shareSelected': {
      // backgroundColor: themeColors.lightBlue,
      backgroundColor: themeColors.lightBlue,
    },
    '& td.hover': {
      backgroundColor: theme.palette.grey[400],
    },
    '& td.shareAtr': {
      backgroundColor: theme.palette.grey[200],
    },
    '& td.initial': {
      backgroundColor: themeColors.lightBlue,
      boxShadow: `inset 0 0 0 4px ${theme.palette.grey[500]}`,
    },
    '& td button': {
      background: 'none',
    },
    '& tbody tr:nth-child(2n)': {
      background: theme.palette.grey[50],
    },
  },

  tableTitle: {
    textDecoration: 'underline',
    display: 'block',
    padding: `${theme.spacing(3)} 0 0`,
  },
};

export type atrRiskValue = 0 | 1 | 2 | 3 | 4 | 5;

const riskValueColumns: atrRiskValue[] = [0, 1, 2, 3, 4, 5];
type riskValues = [
  atrRiskValue | null,
  atrRiskValue | null,
  atrRiskValue | null,
  atrRiskValue | null,
  atrRiskValue | null,
  atrRiskValue | null
];
type riskValueRow = {
  isOptedIn: boolean;
  key: string;
  availableRisks: riskValues;
};

export const getRisk = (
  m: GetModelPortfolios_modelPortfolios | null | undefined
): atrRiskValue => {
  let riskProfile: atrRiskValue = 0;
  switch (m?.riskProfile?.toLocaleLowerCase()) {
    case 'low':
      riskProfile = 1;
      break;
    case 'low to medium':
      riskProfile = 2;
      break;
    case 'medium':
      riskProfile = 3;
      break;
    case 'medium to high':
      riskProfile = 4;
      break;
    case 'high':
      riskProfile = 5;
      break;
    default:
      break;
  }
  return riskProfile;
};

export const convertModelPortfolioToRiskValueRow = (
  models: GetModelPortfolios_modelPortfolios[]
): riskValueRow => {
  const availableRisks: riskValues = [null, null, null, null, null, null];
  models.forEach((m) => {
    const riskProfile = getRisk(m);
    availableRisks[riskProfile] = riskProfile;
  });

  const key = getKey(models[0].providerContact!);
  return {
    key,
    availableRisks,
    isOptedIn: models[0].providerContact?.isOptedIn || false,
  };
};

export type mandateOption = {
  key: string;
  risk: atrRiskValue;
  providerContactLastName: string | null;
  providerContactJobTitle: string | null;
};

function sharesColumnOrRowWithOption(
  column: atrRiskValue | null,
  row: riskValueRow['key'] | null,
  mandateOption: Partial<mandateOption> | null | undefined
) {
  const sharesRow = row !== null && row === mandateOption?.key;
  const sharesColumn = column !== null && column === mandateOption?.risk;
  const sharesBoth = sharesColumn && sharesRow;
  const sharesAny = sharesColumn || sharesRow;
  const sharesEitherButNotBoth = !sharesBoth && sharesAny;
  return {sharesColumn, sharesRow, sharesBoth, sharesEitherButNotBoth, sharesAny};
}

const MandateCell: React.FC<
  PropsWithChildren<{
    atr: atrRiskValue | null;
    column: atrRiskValue | null;
    row: riskValueRow['key'] | null;
    initialOption: mandateOption | null;
    selectedOption: mandateOption | null;
    hoveredOption: Partial<mandateOption> | null;
    onSelect?: (mandateOption: mandateOption) => void;
    onHover: () => SaltusAny;
  }>
> = ({
  atr,
  children,
  column,
  row,
  selectedOption,
  onHover,
  initialOption,
  hoveredOption,
  onSelect,
}) => {
  const selected = sharesColumnOrRowWithOption(column, row, selectedOption);
  const shareHover = sharesColumnOrRowWithOption(column, row, hoveredOption);
  const isInitial = sharesColumnOrRowWithOption(column, row, initialOption).sharesBoth;
  const shareAtr = column !== null && column === atr;
  return (
    <TableCell
      sx={{p: 0.5}}
      onMouseEnter={onHover}
      className={clsx(
        {
          hover: !selected.sharesBoth && shareHover.sharesBoth,
          selected: selected.sharesBoth,
          shareHover: !selected.sharesBoth && shareHover.sharesEitherButNotBoth,
          shareSelected: !selected.sharesBoth && selected.sharesEitherButNotBoth,
          initial: !selectedOption && isInitial,
          shareAtr: !selected.sharesEitherButNotBoth && shareAtr,
        },
        `${camelCase(row || '')}-${column}`
      )}
    >
      {onSelect ? (
        <Button
          sx={{display: 'block', width: '100%'}}
          className={column !== null && row !== null ? 'mandate-option' : ''}
          variant="text"
          onClick={() => {
            if (onSelect) {
              if (column !== null && row !== null) {
                onSelect({
                  key: row,
                  risk: column,
                  providerContactLastName:
                    selectedOption?.providerContactLastName || null,
                  providerContactJobTitle:
                    selectedOption?.providerContactJobTitle || null,
                });
              }
            }
          }}
        >
          {children}
        </Button>
      ) : (
        children
      )}
    </TableCell>
  );
};

const getKey = (providerContact: GetModelPortfolios_modelPortfolios_providerContact) => {
  return `${providerContact.jobTitle}`;
};

const insertModelsIntoDict = (
  m: GetModelPortfolios_modelPortfolios | null,
  dict: Record<string, GetModelPortfolios_modelPortfolios[]>
) => {
  if (!m?.providerContact?.jobTitle) {
    return;
  }
  const key = getKey(m.providerContact);
  if (!(key in dict)) {
    dict[key] = [];
  }
  dict[key] = [...dict[key], m];
};

const riskIndexToStringList = [
  '',
  'low',
  'low to medium',
  'medium',
  'medium to high',
  'high',
];

const getSelectedMandate = (
  portfolios: GetModelPortfolios['modelPortfolios'],
  risk: number,
  key: string
): GetModelPortfolios['modelPortfolios'] => {
  // This is for execution only which does not have a risk profile associated with it
  if (risk === 0) {
    return (
      portfolios?.filter(
        (m) => !m?.riskProfile && m.providerContact && getKey(m.providerContact) === key
      ) || null
    );
  }

  return (
    portfolios?.filter(
      (m) =>
        m?.riskProfile?.toLocaleLowerCase() === riskIndexToStringList[risk] &&
        m.providerContact &&
        getKey(m.providerContact) === key
    ) || null
  );
};

export const Mandates: React.FC<{
  initialRiskValue: mandateOption | null;
  atr: atrRiskValue | null;
  setSelectedBannerName: (name: string) => void;
  setHoverBannerName: (name: string) => void;
  modelPortfolio: modelPortfolioField;
  formikKeyPrefix: string;
  planProvider?: string;
  productName?: string;
  showPanel?: boolean;
  setHoveredPortfolios?: React.Dispatch<
    React.SetStateAction<GetModelPortfolios_modelPortfolios[] | null>
  >;
  setSelectedPortfolios?: React.Dispatch<
    React.SetStateAction<GetModelPortfolios_modelPortfolios[] | null>
  >;
  pot?: PotDetails;
  hasBondParent: boolean;
}> = ({
  initialRiskValue: initialOption,
  atr,
  formikKeyPrefix,
  setHoverBannerName,
  setSelectedBannerName,
  planProvider,
  productName,
  showPanel,
  modelPortfolio,
  setHoveredPortfolios,
  setSelectedPortfolios,
  hasBondParent,
  pot,
}) => {
  const {reviewDetails} = useReviewContext();
  const [selectedOption, setSelectedOption] = useState<mandateOption | null>(null);
  const [hoveredOption, setHoveredOption] = useState<Partial<mandateOption> | null>(null);
  const {setFieldValue, getFieldMeta} = useAutoForm<recommendFieldsType>();
  const {getModelPortfolios} = useModelPortfolios();
  const [centralisedPropositionRows, setCentralisedPropositionRows] = useState<
    riskValueRow[]
  >([]);
  const [panelRows, setPanelRows] = useState<riskValueRow[]>([]);
  const [portfolios, setPortfolios] = useState<GetModelPortfolios['modelPortfolios']>([]);
  const [portfoliosLoading, setPortfoliosLoading] = useState(true);
  const [lockHoveredPortfolios, setLockHoveredPortfolios] = useState(false);
  const {atrName} = useRTQ();
  const unlockHoverIfAlreadySelected = () => {
    if (!getFieldMeta(`${formikKeyPrefix}.modelTemp`)?.value) {
      return;
    }
    setLockHoveredPortfolios(false);
  };

  useEffect(() => {
    setPortfoliosLoading(true);
    getModelPortfolios({
      productNameId: productName,
      productProviderId: planProvider,
      potId: pot?.id,
      hasBondParent,
    })
      .then((p) => {
        setPortfolios(p);
      })
      .finally(() => setPortfoliosLoading(false));
  }, [planProvider, productName]);

  useEffect(() => {
    if (!portfolios || portfolios?.length < 1) {
      return;
    }
    /**
     * business rules
     * CIP - Favourite - YES/ Opted IN - YES
     * Panel- Favourite - YES/ Opted IN - No
     * centralised ones are where isOptedIn == True
     */
    const centralised: Record<string, GetModelPortfolios_modelPortfolios[]> = {};
    portfolios
      .filter((m) => m !== null && m.providerContact?.isOptedIn)
      .forEach((m) => insertModelsIntoDict(m, centralised));
    setCentralisedPropositionRows(
      Object.values(centralised).map(convertModelPortfolioToRiskValueRow)
    );
    // panel ones are where isOptedIn == False
    const panel: Record<string, GetModelPortfolios_modelPortfolios[]> = {};
    portfolios
      .filter((m) => m !== null && !m.providerContact?.isOptedIn)
      .forEach((m) => insertModelsIntoDict(m, panel));
    setPanelRows(Object.values(panel).map(convertModelPortfolioToRiskValueRow));
    // loading initial value if it exists
    if (!modelPortfolio || typeof modelPortfolio === 'string') {
      return;
    }

    const selectedMandate = portfolios.find((m) => m?.name === modelPortfolio.name);

    if (!selectedMandate?.providerContact) {
      return;
    }

    setSelectedOption({
      key: getKey(selectedMandate.providerContact),
      risk: getRisk(selectedMandate),
      providerContactLastName: selectedMandate.providerContact.lastName || null,
      providerContactJobTitle: selectedMandate.providerContact.jobTitle || null,
    });
  }, [portfolios, modelPortfolio]);

  const onSelectMandate = (
    key: string,
    risk: atrRiskValue | null,
    providerContactLastName: string | null,
    providerContactJobTitle: string | null
  ) => {
    if (risk === null) {
      return;
    }
    setSelectedBannerName(`${key} (risk: ${risk})`);
    setSelectedOption({key, risk, providerContactLastName, providerContactJobTitle});
    setLockHoveredPortfolios(false);
    setHoveredPortfolios?.(getSelectedMandate(portfolios, risk || 0, key));
    setSelectedPortfolios?.(getSelectedMandate(portfolios, risk || 0, key));

    const selectedMandates = getSelectedMandate(portfolios, risk, key);

    if (selectedMandates) {
      if (selectedMandates.length > 1) {
        setLockHoveredPortfolios(true);
        setFieldValue(UPDATES._1609, `${formikKeyPrefix}.modelTemp`, null, true);
        return;
      }

      setFieldValue(
        UPDATES._1610,
        `${formikKeyPrefix}.modelTemp`,
        {
          id: selectedMandates[0]?.id,
          name: selectedMandates[0]?.name,
          providerContactLastName: selectedMandates[0].providerContact?.lastName,
          providerContactJobTitle: selectedMandates[0].providerContact?.jobTitle,
        },
        true
      );
    }
  };

  if (!portfolios || portfolios.length < 1) {
    if (portfoliosLoading) {
      return <Loading />;
    }
    return (
      <Alert severity="warning">
        No portfolios available. Please check your selection
      </Alert>
    );
  }

  return (
    <>
      {!atr && !reviewDetails?.contact?.id && (
        <Alert severity="warning">
          This client does not seem to have an {atrName} set up
        </Alert>
      )}
      <Table
        onMouseLeave={() => {
          setHoveredOption(null);
          setHoveredPortfolios?.(null);
          setHoverBannerName('');
        }}
        sx={classes.table}
      >
        <TableHead>
          <TableRow>
            <TableCell sx={{borderBottom: 0}} variant="head" />
            <TableCell
              sx={{borderBottom: 0, position: 'relative'}}
              variant="head"
              colSpan={7}
            >
              <span
                style={{
                  background: 'white',
                  padding: 5,
                  position: 'relative',
                  zIndex: 10,
                }}
              >
                Risk level
              </span>
              <hr
                style={{
                  zIndex: 1,
                  borderBottom: '2px solid grey',
                  position: 'relative',
                  top: '-18px',
                  width: '100%',
                }}
              />
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell
              sx={{borderBottom: 0, position: 'relative', textAlign: 'center'}}
              variant="head"
            />
            {riskValueColumns.map((col) => (
              <MandateCell
                atr={atr}
                key={col}
                column={col}
                row={null}
                selectedOption={selectedOption}
                hoveredOption={hoveredOption}
                initialOption={initialOption}
                onHover={() => {
                  if (lockHoveredPortfolios) {
                    unlockHoverIfAlreadySelected();
                    return;
                  }
                  setHoveredOption({risk: col});
                }}
              >
                <Typography textAlign="center">
                  {atr === col ? (
                    <>
                      {` `}Client {atrName} = {` `}
                      <br />
                    </>
                  ) : null}
                  <strong>{col}</strong>
                </Typography>
              </MandateCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell colSpan={7}>
              <strong style={classes.tableTitle}>Centralised proposition:</strong>
            </TableCell>
          </TableRow>
          {centralisedPropositionRows
            .sort((a, b) => {
              const aKey = a.key?.toLowerCase() || '';
              const bKey = b.key?.toLowerCase() || '';

              if (aKey < bKey) return -1;
              if (aKey > bKey) return 1;
              return 0;
            })
            .map((row) => {
              const {key} = row;
              return (
                <TableRow key={key}>
                  <MandateCell
                    atr={atr}
                    initialOption={initialOption}
                    key={key}
                    column={null}
                    row={key}
                    selectedOption={selectedOption}
                    hoveredOption={hoveredOption}
                    onHover={() => {
                      if (lockHoveredPortfolios) {
                        unlockHoverIfAlreadySelected();
                        return;
                      }
                      setHoveredOption({key});
                    }}
                  >
                    {row.isOptedIn ? '⭐️ ' : ''}
                    {key}
                  </MandateCell>
                  {row.availableRisks.map((risk) => {
                    return (
                      <MandateCell
                        atr={atr}
                        initialOption={initialOption}
                        key={risk}
                        column={risk}
                        row={key}
                        onSelect={() => onSelectMandate(key, risk, null, null)}
                        selectedOption={selectedOption}
                        hoveredOption={hoveredOption}
                        onHover={() => {
                          if (lockHoveredPortfolios) {
                            unlockHoverIfAlreadySelected();
                            return;
                          }
                          setHoveredOption({key, risk: risk ?? undefined});
                          setHoveredPortfolios?.(
                            getSelectedMandate(portfolios, risk || 0, key)
                          );
                          setHoverBannerName(`${key} (risk: ${risk})`);
                        }}
                      >
                        {risk}
                      </MandateCell>
                    );
                  })}
                </TableRow>
              );
            })}
          {showPanel && (
            <TableRow>
              <TableCell colSpan={6}>
                <strong style={classes.tableTitle}>Panel:</strong>
              </TableCell>
            </TableRow>
          )}
          {showPanel &&
            panelRows
              .sort((a, b) => {
                const aKey = a.key?.toLowerCase() || '';
                const bKey = b.key?.toLowerCase() || '';

                const aStructured = aKey.includes('structured');
                const bStructured = bKey.includes('structured');

                if (aStructured && !bStructured) return 1; // a goes after b
                if (!aStructured && bStructured) return -1; // a goes before b

                if (aKey < bKey) return -1;
                if (aKey > bKey) return 1;
                return 0;
              })
              .map((row) => {
                const {key} = row;
                return (
                  <TableRow key={key}>
                    <MandateCell
                      atr={atr}
                      initialOption={initialOption}
                      key={key}
                      column={null}
                      row={key}
                      selectedOption={selectedOption}
                      hoveredOption={hoveredOption}
                      onHover={() => {
                        if (lockHoveredPortfolios) {
                          unlockHoverIfAlreadySelected();
                          return;
                        }
                        setHoveredOption({key});
                      }}
                    >
                      {row.isOptedIn ? '⭐️ ' : ''}
                      {key}
                    </MandateCell>
                    {row.availableRisks.map((risk) => {
                      return (
                        <MandateCell
                          atr={atr}
                          initialOption={initialOption}
                          key={risk}
                          column={risk}
                          row={key}
                          onSelect={() => onSelectMandate(key, risk, null, null)}
                          selectedOption={selectedOption}
                          hoveredOption={hoveredOption}
                          onHover={() => {
                            if (lockHoveredPortfolios) {
                              unlockHoverIfAlreadySelected();
                              return;
                            }
                            setHoveredOption({key, risk: risk ?? undefined});
                            setHoveredPortfolios?.(
                              getSelectedMandate(portfolios, risk || 0, key)
                            );
                            setHoverBannerName(`${key} (risk: ${risk})`);
                          }}
                        >
                          {risk}
                        </MandateCell>
                      );
                    })}
                  </TableRow>
                );
              })}
        </TableBody>
      </Table>
    </>
  );
};
